interface ResultsPanelProps {
  oldAnnualCost: number
  newAnnualCost: number
  annualSavings: number
  tenYearSavings: number
  co2Saved: number
  paybackYears: number | null
  annualKWhSaved: number
  accentColor: 'blue' | 'orange'
  oldLabel: string
  newLabel: string
}

export default function ResultsPanel({
  oldAnnualCost,
  newAnnualCost,
  annualSavings,
  tenYearSavings,
  co2Saved,
  paybackYears,
  annualKWhSaved,
  accentColor,
  oldLabel,
  newLabel,
}: ResultsPanelProps) {
  const isBlue = accentColor === 'blue'
  const accent = isBlue ? 'blue' : 'orange'
  const accentGreen = 'green'

  // Bar chart: scale bars relative to the higher cost
  const maxCost = Math.max(oldAnnualCost, newAnnualCost, 0.01)
  const oldBarPct = (oldAnnualCost / maxCost) * 100
  const newBarPct = (newAnnualCost / maxCost) * 100

  const efficiencyGain =
    oldAnnualCost > 0
      ? ((oldAnnualCost - newAnnualCost) / oldAnnualCost) * 100
      : 0

  const fmt = (n: number) =>
    n.toLocaleString('en-US', { maximumFractionDigits: 0 })

  return (
    <div
      className="space-y-5"
      role="region"
      aria-label="Energy savings calculation results"
    >
      {/* Bar Chart */}
      <div
        className="rounded-xl border border-slate-700/50 bg-slate-950/60 p-5"
        aria-label="Annual operating cost comparison bar chart"
      >
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">
          Annual Operating Cost Comparison
        </h4>

        <div className="space-y-3">
          {/* Old system bar */}
          <div role="group" aria-label={`${oldLabel} cost`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-400">{oldLabel}</span>
              <span
                aria-live="polite"
                className="text-xs font-bold tabular-nums text-red-400"
              >
                ${fmt(oldAnnualCost)}/yr
              </span>
            </div>
            <div className="h-7 bg-slate-800/50 rounded-lg overflow-hidden">
              <div
                className="h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-500 ease-out bg-gradient-to-r from-red-700/60 to-red-600/80"
                style={{ width: `${oldBarPct}%` }}
                aria-hidden="true"
              />
            </div>
          </div>

          {/* New system bar */}
          <div role="group" aria-label={`${newLabel} cost`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-400">{newLabel}</span>
              <span
                aria-live="polite"
                className={`text-xs font-bold tabular-nums text-${accent}-400`}
              >
                ${fmt(newAnnualCost)}/yr
              </span>
            </div>
            <div className="h-7 bg-slate-800/50 rounded-lg overflow-hidden">
              <div
                className={`h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-500 ease-out ${
                  isBlue
                    ? 'bg-gradient-to-r from-blue-700/60 to-blue-500/80'
                    : 'bg-gradient-to-r from-orange-700/60 to-orange-500/80'
                }`}
                style={{ width: `${newBarPct}%` }}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        {/* Efficiency gain badge */}
        <div
          aria-label={`${efficiencyGain.toFixed(0)}% more efficient`}
          className="mt-4 flex items-center justify-center"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold">
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            {efficiencyGain.toFixed(0)}% more efficient
          </div>
        </div>
      </div>

      {/* Savings Stats Grid */}
      <div className="grid grid-cols-2 gap-3" aria-label="Savings metrics">
        {/* Annual Savings */}
        <div className={`rounded-xl border p-4 ${
          isBlue ? 'border-blue-500/20 bg-blue-500/5' : 'border-orange-500/20 bg-orange-500/5'
        }`}>
          <p className="text-xs font-medium text-slate-500 mb-1">Annual Savings</p>
          <p
            aria-live="polite"
            className={`text-2xl font-extrabold tabular-nums ${
              isBlue ? 'text-blue-300' : 'text-orange-300'
            }`}
          >
            ${fmt(annualSavings)}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">per year</p>
        </div>

        {/* 10-Year Savings */}
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">10-Year Savings</p>
          <p
            aria-live="polite"
            className="text-2xl font-extrabold tabular-nums text-green-300"
          >
            ${fmt(tenYearSavings)}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">over 10 years</p>
        </div>

        {/* CO2 Reduction */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">CO₂ Reduction</p>
          <p
            aria-live="polite"
            className="text-2xl font-extrabold tabular-nums text-emerald-300"
          >
            {fmt(co2Saved)}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">lbs CO₂/yr</p>
        </div>

        {/* Payback Period */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">ROI Payback</p>
          <p
            aria-live="polite"
            className="text-2xl font-extrabold tabular-nums text-slate-200"
          >
            {paybackYears !== null ? `${paybackYears.toFixed(1)} yrs` : '—'}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">
            {paybackYears !== null ? 'to break even' : 'no cost entered'}
          </p>
        </div>
      </div>

      {/* kWh savings */}
      <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <svg
            className="w-4 h-4 text-yellow-500 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <span>Annual electricity saved</span>
        </div>
        <span
          aria-live="polite"
          aria-label={`${fmt(annualKWhSaved)} kilowatt hours saved per year`}
          className="text-sm font-bold tabular-nums text-yellow-400"
        >
          {fmt(annualKWhSaved)} kWh/yr
        </span>
      </div>
    </div>
  )
}
